import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { MainState } from "@app/store/state";
import { TagInterface } from "@core/interfaces/forums/tag.interface";
import { TopicTagInterface } from "@core/interfaces/forums/topic.interface";
import { ForumApiService } from "@core/services/api/forum/forum-api.service";
import { ClassicRoutesService } from "@core/services/classic-routes.service";
import { UtilsService } from "@core/services/utils/utils.service";
import { WindowRefService } from "@core/services/window-ref.service";
import { TagGenerationService } from "@features/forum/services/tag-generation.service";
import { NgSelectComponent } from "@ng-select/ng-select";
import { Store } from "@ngrx/store";
import { FieldType } from "@ngx-formly/core";
import { BehaviorSubject, EMPTY, Subject } from "rxjs";
import { debounceTime, distinctUntilChanged, switchMap, takeUntil, tap } from "rxjs/operators";

@Component({
  selector: "astrobin-formly-field-tags-with-ai",
  templateUrl: "./formly-field-tags-with-ai.component.html",
  styleUrls: ["./formly-field-tags-with-ai.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FormlyFieldTagsWithAiComponent extends FieldType implements OnInit, OnDestroy {
  @ViewChild("tagSelect") ngSelect: NgSelectComponent;

  // Browse mode
  browseMode: "alphabetical" | "category" | "popularity" = "alphabetical";
  categoryGroups: Map<string, TagInterface[]> = new Map();

  isTagsAiGenerated = false;
  isTagsGenerating$: BehaviorSubject<boolean>;
  canGenerateTags = false;
  hasBodyChangedSinceLastGeneration = false;
  tagOptions$ = new BehaviorSubject<(TagInterface & { group: string })[]>(null);
  isAutoGenerationEnabled = true; // Always enabled now

  isLoadingTags = false;
  currentTagPage = 1;
  hasMoreTags = true;
  currentSearchTerm = "";
  currentSection: "non-equipment" | "equipment" = "non-equipment";
  nonEquipmentTagsComplete = false;
  equipmentTagPage = 1;
  categoryModeNonEquipmentComplete = false;

  // Suggestion state
  hasPendingSuggestions = false;
  pendingSuggestions: TopicTagInterface[] = [];

  // Dropdown position
  dropdownPosition: "auto" | "top" | "bottom" = "auto";
  // For tracking whether to display the field in DOM
  hasContent = false;
  protected readonly input$ = new Subject<string>();
  // Tracking for rejected tags
  private _allSuggestedTags: TopicTagInterface[] = [];
  private readonly _destroy$ = new Subject<void>();
  private _userEnteredTags = [];
  private _tagsManuallyEdited = false;
  private _lastProcessedBodyContent = "";

  constructor(
    private readonly tagGenerationService: TagGenerationService,
    private readonly forumApiService: ForumApiService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly utilsService: UtilsService,
    private readonly store: Store<MainState>,
    private readonly classicRoutesService: ClassicRoutesService,
    private readonly windowRefService: WindowRefService
  ) {
    super();
    this.isTagsGenerating$ = this.tagGenerationService.isGenerating$;
  }

  /**
   * Check if the control should be in read-only state
   */
  get isReadOnly(): boolean {
    return this.formControl.disabled;
  }

  ngOnInit(): void {
    // Set dropdown position from props if provided
    if (this.props.dropdownPosition) {
      this.dropdownPosition = this.props.dropdownPosition;
    }

    // Setup change detection on the tag generation state
    this.isTagsGenerating$.pipe(takeUntil(this._destroy$)).subscribe(() => this.changeDetectorRef.markForCheck());

    // Listen for body content updates from parent through field definition
    if (this.props.bodyContent$) {
      this.props.bodyContent$.pipe(takeUntil(this._destroy$)).subscribe(content => {
        // Check if content meets minimum requirements
        this.canGenerateTags = !!content && content.length >= TagGenerationService.MIN_CONTENT_LENGTH;

        // Track content changes and pass auto-generation flag to service
        // This ensures the service knows whether to process the content or not
        this.tagGenerationService.processContentChange(content, this.isAutoGenerationEnabled);

        // Update change tracking only if content has changed
        const contentHasChanged = this._checkForContentChanges(content);

        this.changeDetectorRef.markForCheck();
      });
    }

    // Also listen for changes in the parent form if available
    if (this.props.form) {
      this.props.form.valueChanges.pipe(takeUntil(this._destroy$)).subscribe(() => {
        // Update the canGenerateTags flag based on current form value
        if (this.props.form && this.props.form.value && this.props.form.value.body) {
          const bodyContent = this.props.form.value.body;
          this.canGenerateTags = bodyContent.length >= TagGenerationService.MIN_CONTENT_LENGTH;
          this._checkForContentChanges(bodyContent);
          this.changeDetectorRef.markForCheck();
        }
      });
    }

    // Subscribe to automatically generated tags
    this.tagGenerationService.autoGeneratedTags$.pipe(takeUntil(this._destroy$)).subscribe(tags => {
      // Show as suggestions if:
      // 1. Field is empty (no tags), OR
      // 2. It's already an AI tags that hasn't been manually edited
      // This ensures we respect user edits unless they click refresh
      if (
        !this.formControl.value ||
        (Array.isArray(this.formControl.value) && this.formControl.value.length === 0) ||
        (this.isTagsAiGenerated && !this._tagsManuallyEdited)
      ) {
        // Show as suggestions instead of directly setting value
        if (tags && tags.length > 0) {
          this.pendingSuggestions = tags;
          this.hasPendingSuggestions = true;

          // Track all suggested tags
          this._allSuggestedTags = [...tags];

          // Notify parent about suggestions
          if (this.props.onTagsSuggested) {
            this.props.onTagsSuggested(tags);
          }
        }

        // Get the current body content
        let currentBodyContent = "";
        if (this.props.form && this.props.form.value && this.props.form.value.body) {
          currentBodyContent = this.props.form.value.body;
        } else if (typeof this.props.bodyContent === "function") {
          currentBodyContent = this.props.bodyContent();
        } else if (this.props.bodyContent) {
          currentBodyContent = this.props.bodyContent;
        }

        // Store the processed content to track future changes
        if (currentBodyContent) {
          this._lastProcessedBodyContent = currentBodyContent;
          this.hasBodyChangedSinceLastGeneration = false;
        }

        this.changeDetectorRef.detectChanges();
      }
    });

    // Initialize field state if there's a value
    if (this.formControl.value) {
      this.hasContent = true;

      // Set AI generated flag if provided through field props
      if (this.props.isAiGenerated !== undefined) {
        this.isTagsAiGenerated = this.props.isAiGenerated;
      }

      // Set manually edited flag if provided
      if (this.props.isManuallyEdited !== undefined) {
        this._tagsManuallyEdited = this.props.isManuallyEdited;
      }
    }

    // Check initial body content when component is first initialized
    this.utilsService.delay(0).subscribe(() => {
      // Get initial body content
      let initialBodyContent = "";
      if (this.props.form && this.props.form.value && this.props.form.value.body) {
        initialBodyContent = this.props.form.value.body;
      } else if (typeof this.props.bodyContent === "function") {
        initialBodyContent = this.props.bodyContent();
      } else if (this.props.bodyContent) {
        initialBodyContent = this.props.bodyContent;
      }

      // Update canGenerateTags based on initial content
      if (initialBodyContent && initialBodyContent.length >= TagGenerationService.MIN_CONTENT_LENGTH) {
        this.canGenerateTags = true;
        this._lastProcessedBodyContent = initialBodyContent;
        this.changeDetectorRef.markForCheck();
      }
    });

    // Set up tag search - ALWAYS query server
    this.input$
      .pipe(
        takeUntil(this._destroy$),
        tap(searchTerm => {
          // Show loading immediately when typing starts
          if (searchTerm) {
            // Set loading and use null to indicate loading state
            this.isLoadingTags = true;
            this.tagOptions$.next(null);
            this.changeDetectorRef.detectChanges();
          } else {
            // Empty search - use null to trigger loading of default tags
            this.tagOptions$.next(null);
            this.changeDetectorRef.detectChanges();
          }
        }),
        debounceTime(300),
        distinctUntilChanged(),
        switchMap(searchTerm => {
          // Process search term for catalog objects
          let processedSearchTerm = searchTerm || "";

          if (processedSearchTerm) {
            // List of catalog prefixes
            const catalogPrefixes = ["NGC", "IC", "M", "SH2", "VDB", "LDN", "LBN"];

            // Check if search term matches catalog pattern (e.g., "M 31", "ngc 1234", "SH2 101")
            const catalogPattern = new RegExp(`^(${catalogPrefixes.join("|")})\\s+(\\d+)`, "i");
            const match = processedSearchTerm.match(catalogPattern);

            if (match) {
              // Remove space between catalog name and number
              processedSearchTerm = match[1].toUpperCase() + match[2];
            }
          }

          this.currentSearchTerm = processedSearchTerm;
          this.currentTagPage = 1;
          this.hasMoreTags = true;

          // Always query server
          if (processedSearchTerm) {
            // Keep loading state active for API call
            this.isLoadingTags = true;
            this.changeDetectorRef.markForCheck();

            // Return the API observable - switchMap will cancel previous one
            const ordering = "name";
            const excludeCategories = "equipment";
            return this.forumApiService.getTags(1, processedSearchTerm, undefined, ordering, excludeCategories);
          } else {
            // No search term - load default tags
            this.isLoadingTags = false;
            this._loadTagsByMode();
            return EMPTY; // Complete immediately for no search
          }
        })
      )
      .subscribe({
        next: response => {
          const searchTerm = this.currentSearchTerm;
          const hasResults = response && response.results && response.results.length > 0;

          if (hasResults) {
            // When searching, don't show group headers
            const groupedTags = searchTerm
              ? response.results.map(tag => ({ ...tag, group: null }))
              : response.results.map(tag => ({ ...tag, group: "General Tags (A-Z)" }));

            // Set results and clear loading in one synchronous operation
            this.tagOptions$.next(groupedTags);
            this.hasMoreTags = !!response.next;
            this.nonEquipmentTagsComplete = !response.next;
            this.isLoadingTags = false;

            // Single change detection cycle
            this.changeDetectorRef.detectChanges();
          } else {
            // No results - set empty array (not null) to show "No tags found"
            this.tagOptions$.next([]);
            this.isLoadingTags = false;
            this.changeDetectorRef.detectChanges();
          }
        },
        error: () => {
          // Error - set empty array (not null) to show "No tags found"
          this.tagOptions$.next([]);
          this.isLoadingTags = false;
          this.changeDetectorRef.detectChanges();
        }
      });

    // Load initial tags when dropdown opens (no search term)
    this._loadTagsByMode();
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  /**
   * Triggered when user manually edits the tags
   */
  onTagsChange(): void {
    const currentValue = this.formControl.value || [];
    const previousValue = this._userEnteredTags || [];

    this.hasContent = currentValue.length > 0;

    // Check if any tags were removed
    const removedTags = previousValue.filter(
      prevTag => !currentValue.some((currTag: TopicTagInterface) => currTag.slug === prevTag.slug)
    );

    // Notify parent about removed tags that were originally suggested
    if (this.props.onTagRejected && removedTags.length > 0) {
      removedTags.forEach(tag => {
        // Only notify if this tag was part of the original suggestions
        if (this._allSuggestedTags.some(suggested => suggested.slug === tag.slug)) {
          this.props.onTagRejected(tag);
        }
      });
    }

    // Store current tags for next comparison
    this._userEnteredTags = [...currentValue];

    // If we had AI-generated tags and user is editing them,
    // update the state to prevent auto-updates to the tags
    if (this.isTagsAiGenerated) {
      this.isTagsAiGenerated = false;
      this._tagsManuallyEdited = true;
      this.changeDetectorRef.markForCheck();
    }

    // Call parent handler if provided
    if (this.props.onTagsChange) {
      // The formControl.value contains full TagInterface objects as selected via ng-select
      this.props.onTagsChange(this.formControl.value, this.isTagsAiGenerated, this._tagsManuallyEdited);
    }
  }

  /**
   * Generate tags using the AI service
   * @param isAutoGenerated true if this is triggered by auto-generation, false if user clicked the button
   */
  generateTags(isAutoGenerated: boolean = false): void {
    // If this is an auto-generated request and auto-generation is disabled, do nothing
    // Also prevent generation if form control is disabled
    if ((isAutoGenerated && !this.isAutoGenerationEnabled) || this.isReadOnly) {
      return;
    }
    // Get the current body content using the getter if available,
    // or directly from props if not
    let bodyContent = "";

    if (typeof this.props.bodyContent === "function") {
      // If bodyContent is a getter function
      bodyContent = this.props.bodyContent();
    } else if (this.props.form && this.props.form.value && this.props.form.value.body) {
      // If we have a reference to the parent form
      bodyContent = this.props.form.value.body;
    } else if (this.props.bodyContent) {
      // Fallback to static value
      bodyContent = this.props.bodyContent;
    }

    // Don't proceed if generation is already in progress
    if (this.isGenerating()) {
      return;
    }

    // Save the current body content to track changes
    this._lastProcessedBodyContent = bodyContent;
    this.hasBodyChangedSinceLastGeneration = false;

    // If content is less than minimum required length, clear the tags
    if (!bodyContent || bodyContent.length < TagGenerationService.MIN_CONTENT_LENGTH) {
      this.formControl.setValue([], { onlySelf: true, emitEvent: false });
      this.hasContent = false;
      this.isTagsAiGenerated = false;
      this._tagsManuallyEdited = false;

      // Call parent handler if provided
      if (this.props.onTagsChange) {
        this.props.onTagsChange([], false, false);
      }

      this.changeDetectorRef.detectChanges();
      return;
    }

    // Remember user entered tags if we had some
    if (this.formControl.value && !this.isTagsAiGenerated) {
      this._userEnteredTags = [...this.formControl.value];
    }

    // Request tags generation
    this.tagGenerationService.generateTagsManually(bodyContent).subscribe({
      next: tags => {
        if (tags && tags.length > 0) {
          // Show as suggestions instead of directly setting value
          this.pendingSuggestions = tags;
          this.hasPendingSuggestions = true;

          // Force change detection
          this.changeDetectorRef.detectChanges();
        }
      },
      error: error => {
        console.error("Error during tags generation:", error);
      }
    });
  }

  /**
   * Handle generate tags button click
   */
  onGenerateTagsClick(tooltip: any): void {
    if (!this.canGenerateTags || this.isReadOnly) {
      return;
    }
    this.generateTags();
    tooltip.close();
  }

  /**
   * Handle scroll to end of ng-select dropdown for pagination
   */
  onSelectScrollToEnd(): void {
    if (this.isLoadingTags) {
      return;
    }

    // Handle pagination based on browse mode
    if (this.browseMode === "alphabetical") {
      if (this.currentSection === "non-equipment" && this.nonEquipmentTagsComplete) {
        // Switch to equipment tags
        this.currentSection = "equipment";
        this._resetAndLoadEquipmentTags();
      } else if (this.currentSection === "non-equipment" && this.hasMoreTags) {
        // Continue loading non-equipment tags
        this._loadMoreNonEquipmentTags();
      } else if (this.currentSection === "equipment" && this.hasMoreTags) {
        // Continue loading equipment tags
        this._loadMoreEquipmentTags();
      }
    } else if (this.browseMode === "popularity" && this.hasMoreTags) {
      // Load more popular tags
      this.currentTagPage++;
      this._loadTagsForPopularityMode();
    } else if (this.browseMode === "category") {
      if (this.currentSection === "non-equipment" && this.categoryModeNonEquipmentComplete) {
        // Switch to equipment tags
        this.currentSection = "equipment";
        this.currentTagPage = 1;
        this._loadTagsForCategoryMode();
      } else if (this.hasMoreTags) {
        // Continue loading current section
        this.currentTagPage++;
        this._loadTagsForCategoryMode();
      }
    }
  }

  /**
   * Accept a single tag suggestion
   */
  acceptSuggestion(tagSuggestion: TopicTagInterface): void {
    if (this.isReadOnly) {
      return;
    }

    const currentValue = this.formControl.value || [];
    const tagAlreadySelected = currentValue.some(
      (selectedTag: TopicTagInterface) => selectedTag.slug === tagSuggestion.slug
    );

    if (!tagAlreadySelected) {
      // Add the suggestion directly - it has the required slug and name
      const newValue = [...currentValue, tagSuggestion];
      this.formControl.setValue(newValue);
      this.hasContent = true;
      this.isTagsAiGenerated = true;
      this._tagsManuallyEdited = false;

      // Update user entered tags
      this._userEnteredTags = [...newValue];

      // Call parent handler if provided
      if (this.props.onTagsChange) {
        this.props.onTagsChange(newValue, true, false);
      }
    }

    // Remove from suggestions
    this.pendingSuggestions = this.pendingSuggestions.filter(s => s.slug !== tagSuggestion.slug);
    if (this.pendingSuggestions.length === 0) {
      this.hasPendingSuggestions = false;
    }

    this.changeDetectorRef.detectChanges();
  }

  /**
   * Reject a single tag suggestion
   */
  rejectSuggestion(tagSuggestion: TopicTagInterface): void {
    // Remove from suggestions
    this.pendingSuggestions = this.pendingSuggestions.filter(s => s.slug !== tagSuggestion.slug);
    if (this.pendingSuggestions.length === 0) {
      this.hasPendingSuggestions = false;
    }

    // Notify parent about rejection
    if (this.props.onTagRejected) {
      this.props.onTagRejected(tagSuggestion);
    }

    this.changeDetectorRef.detectChanges();
  }

  /**
   * Open contact form for suggesting new tags
   */
  onSuggestNewTag(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();

    this.windowRefService.nativeWindow.open(this.classicRoutesService.CONTACT, "_blank");
  }

  /**
   * Set browse mode and reload tags
   */
  setBrowseMode(mode: "alphabetical" | "category" | "popularity"): void {
    if (this.browseMode === mode) {
      return;
    }

    this.browseMode = mode;
    this.currentTagPage = 1;
    this.hasMoreTags = true;
    this.currentSection = "non-equipment";
    this.nonEquipmentTagsComplete = false;
    this.categoryModeNonEquipmentComplete = false;
    this.equipmentTagPage = 1;
    this._loadTagsByMode();
  }

  /**
   * Handle mode change from dropdown - using mousedown to prevent dropdown from closing
   */
  onModeChange(event: MouseEvent, mode: "alphabetical" | "category" | "popularity"): void {
    event.preventDefault();
    event.stopPropagation();
    this.setBrowseMode(mode);
  }

  onCloseDropdown(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.ngSelect) {
      this.ngSelect.close();
    }
  }

  /**
   * Get tag description with equipment type support
   */
  getTagDescription(tag: TagInterface | TopicTagInterface): string {
    return this.forumApiService.getTagDescription(tag);
  }

  /**
   * Get confidence level category for styling
   */
  getConfidenceLevel(confidence: number): string {
    if (confidence >= 0.9) {
      return "high";
    }
    if (confidence >= 0.7) {
      return "medium";
    }
    return "low";
  }

  // Helper method to safely check if generation is in progress
  private isGenerating(): boolean {
    return this.tagGenerationService.isGenerating$.getValue();
  }

  /**
   * Check if body content has changed since the last generation
   * @returns true if content has changed, false otherwise
   */
  private _checkForContentChanges(content: string): boolean {
    const contentChanged = content !== this._lastProcessedBodyContent;

    // If content is different, mark as changed
    if (contentChanged) {
      this.hasBodyChangedSinceLastGeneration = true;
      this.changeDetectorRef.markForCheck();
      return true;
    }

    return false;
  }

  /**
   * Load tags with pagination support - resets to start with non-equipment tags
   */
  private _loadTags(reset: boolean): void {
    if (reset) {
      this.currentSection = "non-equipment";
      this.nonEquipmentTagsComplete = false;
      this.currentTagPage = 1;
      this.equipmentTagPage = 1;
      this._resetAndLoadNonEquipmentTags();
    } else {
      this._loadMoreNonEquipmentTags();
    }
  }

  /**
   * Reset and load first page of non-equipment tags
   */
  private _resetAndLoadNonEquipmentTags(): void {
    const searchTerm = this.currentSearchTerm || undefined;
    const ordering = "name";
    // Exclude both equipment and celestial-objects when not searching
    const excludeCategories = searchTerm ? "equipment" : "equipment,celestial-objects";

    this.isLoadingTags = true;
    this.currentTagPage = 1;

    this.forumApiService.getTags(1, searchTerm, undefined, ordering, excludeCategories).subscribe({
      next: response => {
        const hasResults = response && response.results && response.results.length > 0;

        if (hasResults) {
          // When searching, don't show group headers
          const groupedTags = searchTerm
            ? response.results.map(tag => ({ ...tag, group: null }))
            : response.results.map(tag => ({ ...tag, group: "General Tags (A-Z)" }));
          this.tagOptions$.next(groupedTags);
          this.hasMoreTags = !!response.next;
          this.nonEquipmentTagsComplete = !response.next;
        } else {
          // No results - set empty array (not null) to show "No tags found"
          this.tagOptions$.next([]);
        }

        // Clear loading state now that we have a response
        this.isLoadingTags = false;
        this.changeDetectorRef.detectChanges();
      },
      error: () => {
        // Error - set empty array (not null) to show "No tags found"
        this.tagOptions$.next([]);
        this.isLoadingTags = false;
        this.changeDetectorRef.detectChanges();
      }
    });
  }

  /**
   * Load more non-equipment tags (pagination)
   */
  private _loadMoreNonEquipmentTags(): void {
    if (this.isLoadingTags) {
      return;
    }

    const page = this.currentTagPage + 1;
    const searchTerm = this.currentSearchTerm || undefined;
    const ordering = "name";
    // Exclude both equipment and celestial-objects when not searching
    const excludeCategories = searchTerm ? "equipment" : "equipment,celestial-objects";

    this.isLoadingTags = true;

    this.forumApiService.getTags(page, searchTerm, false, ordering, excludeCategories).subscribe({
      next: response => {
        if (response && response.results) {
          // When searching, don't show group headers
          const groupedTags = searchTerm
            ? response.results.map(tag => ({ ...tag, group: null }))
            : response.results.map(tag => ({ ...tag, group: "General Tags (A-Z)" }));
          const currentTags = this.tagOptions$.value || [];
          const allTags = [...currentTags, ...groupedTags];
          const uniqueTags = allTags.filter((tag, index, arr) => arr.findIndex(t => t.id === tag.id) === index);
          this.tagOptions$.next(uniqueTags);
          this.currentTagPage = page;

          this.hasMoreTags = !!response.next;
          if (!response.next) {
            this.nonEquipmentTagsComplete = true;
          }
        }
        // Clear loading and update view in single cycle
        this.isLoadingTags = false;
        this.changeDetectorRef.detectChanges();
      },
      error: () => {
        // Error - set empty array (not null) to show "No tags found"
        this.tagOptions$.next([]);
        this.isLoadingTags = false;
        this.changeDetectorRef.detectChanges();
      }
    });
  }

  /**
   * Reset and load first page of equipment tags
   */
  private _resetAndLoadEquipmentTags(): void {
    const searchTerm = this.currentSearchTerm || undefined;
    const ordering = "name";
    const category = "equipment";

    this.isLoadingTags = true;
    this.equipmentTagPage = 1;

    this.forumApiService.getTags(1, searchTerm, false, ordering, undefined, category).subscribe({
      next: response => {
        if (response && response.results) {
          // When searching, don't show group headers
          const groupedTags = searchTerm
            ? response.results.map(tag => ({ ...tag, group: null }))
            : response.results.map(tag => ({ ...tag, group: "Equipment Tags (A-Z)" }));
          const currentTags = this.tagOptions$.value || [];
          const allTags = [...currentTags, ...groupedTags];
          const uniqueTags = allTags.filter((tag, index, arr) => arr.findIndex(t => t.id === tag.id) === index);
          this.tagOptions$.next(uniqueTags);
          this.hasMoreTags = !!response.next;
        } else {
          // Keep existing tags if no new results (don't change to null or empty)
          // This preserves the current list when there are no more pages
        }
        // Clear loading and update view in single cycle
        this.isLoadingTags = false;
        this.changeDetectorRef.detectChanges();
      },
      error: () => {
        // Error - set empty array (not null) to show "No tags found"
        this.tagOptions$.next([]);
        this.isLoadingTags = false;
        this.changeDetectorRef.detectChanges();
      }
    });
  }

  /**
   * Load more equipment tags (pagination)
   */
  private _loadMoreEquipmentTags(): void {
    if (this.isLoadingTags) {
      return;
    }

    const page = this.equipmentTagPage + 1;
    const searchTerm = this.currentSearchTerm || undefined;
    const ordering = "name";
    const category = "equipment";

    this.isLoadingTags = true;

    this.forumApiService.getTags(page, searchTerm, undefined, ordering, undefined, category).subscribe({
      next: response => {
        if (response && response.results) {
          // When searching, don't show group headers
          const groupedTags = searchTerm
            ? response.results.map(tag => ({ ...tag, group: null }))
            : response.results.map(tag => ({ ...tag, group: "Equipment Tags (A-Z)" }));
          const currentTags = this.tagOptions$.value || [];
          const allTags = [...currentTags, ...groupedTags];
          const uniqueTags = allTags.filter((tag, index, arr) => arr.findIndex(t => t.id === tag.id) === index);
          this.tagOptions$.next(uniqueTags);
          this.equipmentTagPage = page;
          this.hasMoreTags = !!response.next;
        }
        // Clear loading and update view in single cycle
        this.isLoadingTags = false;
        this.changeDetectorRef.detectChanges();
      },
      error: () => {
        // Error - set empty array (not null) to show "No tags found"
        this.tagOptions$.next([]);
        this.isLoadingTags = false;
        this.changeDetectorRef.detectChanges();
      }
    });
  }

  /**
   * Load tags based on current browse mode
   */
  private _loadTagsByMode(): void {
    switch (this.browseMode) {
      case "alphabetical":
        this._loadTags(true);
        break;
      case "category":
        this._loadTagsForCategoryMode();
        break;
      case "popularity":
        this._loadTagsForPopularityMode();
        break;
    }
  }

  /**
   * Load tags for category mode
   */
  private _loadTagsForCategoryMode(): void {
    this.isLoadingTags = true;

    let ordering: string;
    let excludeCategories: string | undefined;
    let category: string | undefined;

    if (this.currentSection === "non-equipment") {
      // Load non-equipment tags sorted by category, then name
      ordering = "category,name";
      // Always exclude celestial-objects in category mode since they're too many
      excludeCategories = "equipment,celestial-objects";
    } else {
      // Load equipment tags sorted by name only
      ordering = "name";
      category = "equipment";
    }

    this.forumApiService
      .getTags(this.currentTagPage, undefined, false, ordering, excludeCategories, category)
      .subscribe({
        next: response => {
          if (response && response.results) {
            const groupedTags = this._groupTagsByCategory(response.results);

            if (this.currentTagPage === 1 && this.currentSection === "non-equipment") {
              // First page of non-equipment tags
              this.tagOptions$.next(groupedTags);
            } else {
              // Append to existing tags
              const currentTags = this.tagOptions$.value || [];
              this.tagOptions$.next([...currentTags, ...groupedTags]);
            }

            this.hasMoreTags = !!response.next;

            // Mark non-equipment section as complete when no more pages
            if (this.currentSection === "non-equipment" && !response.next) {
              this.categoryModeNonEquipmentComplete = true;
              this.hasMoreTags = true; // Reset for equipment section
            }
          }
          // Keep loading state active until results are fully rendered
          // Clear loading and update view in single cycle
          this.isLoadingTags = false;
          this.changeDetectorRef.detectChanges();
        },
        error: () => {
          this.isLoadingTags = false;
          this.changeDetectorRef.markForCheck();
        }
      });
  }

  /**
   * Group tags by category for display
   */
  private _groupTagsByCategory(tags: TagInterface[]): (TagInterface & { group: string })[] {
    const grouped: (TagInterface & { group: string })[] = [];
    const categoryMap = new Map<string, TagInterface[]>();

    // Group tags by category
    tags.forEach(tag => {
      const category = tag.category || "uncategorized";
      if (!categoryMap.has(category)) {
        categoryMap.set(category, []);
      }
      categoryMap.get(category)!.push(tag);
    });

    // Sort categories alphabetically
    const sortedCategories = Array.from(categoryMap.keys()).sort((a, b) => {
      return a.localeCompare(b);
    });

    sortedCategories.forEach(category => {
      const categoryTags = categoryMap.get(category)!;
      // Convert category name to title case
      const categoryName = this._toTitleCase(category);

      categoryTags.forEach(tag => {
        grouped.push({
          ...tag,
          group: categoryName
        });
      });
    });

    return grouped;
  }

  /**
   * Convert a string to title case
   */
  private _toTitleCase(str: string): string {
    return str
      .split("-")
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  }

  /**
   * Load tags for popularity mode
   */
  private _loadTagsForPopularityMode(): void {
    this.isLoadingTags = true;

    // Always exclude celestial-objects in popularity mode since they're too many
    const excludeCategories = "celestial-objects";

    this.forumApiService
      .getTags(this.currentTagPage, undefined, undefined, "-topic_count", excludeCategories)
      .subscribe({
        next: response => {
          if (response && response.results) {
            const groupedTags = response.results.map(tag => ({
              ...tag,
              group: "Most Used Tags"
            }));

            if (this.currentTagPage === 1) {
              this.tagOptions$.next(groupedTags);
            } else {
              const currentTags = this.tagOptions$.value || [];
              this.tagOptions$.next([...currentTags, ...groupedTags]);
            }

            this.hasMoreTags = !!response.next;
          }
          // Keep loading state active until results are fully rendered
          // Clear loading and update view in single cycle
          this.isLoadingTags = false;
          this.changeDetectorRef.detectChanges();
        },
        error: () => {
          this.isLoadingTags = false;
          this.changeDetectorRef.markForCheck();
        }
      });
  }
}
